import {COMP_ARRAY, COMP_ARRAYS, COMP_FIELDS} from '@/constants/comp';
import {INVE_FIELDS} from '@/constants/inve';
import {PRV_COMP_FIELDS} from '@/constants/prv-comp';
import {isDocumentCreatedFromPrefilledInvestor, isPrefilledClient} from '@/model/helpers/section-inve';
import {Booleans} from '@/model/src/values/dv-boolean';
import {ArrayFilter, Condition, GeneratedValue} from '@/model/types';
import {AllocationMethod} from '../src/values/dv-102';
import {Contributor} from '../src/values/dv-11';
import {ParticipantOrExSpouse} from '../src/values/dv-12';
import {Plans} from '../src/values/dv-9';
import {ContractTypes} from '../src/values/dv-93';
import {Conditions} from './conditions';
import {and, not, or} from './conditions/utils';
import {Filters} from './filters';
import {buildSourcePlanRules, hasAnyExistingAccountAllowedForRule} from './source-plan-rules';
import {buildAmountOrPercentageTemplateValue} from './template';

export const isDocumentCreatedFromPrefilledInvestorWithAccount = and(
  isDocumentCreatedFromPrefilledInvestor,
  Conditions.fields.isFilled(COMP_FIELDS.COMP1_accountNo),
) satisfies Condition;

export const isPrefilledTransaction = and(
  isPrefilledClient,
  isDocumentCreatedFromPrefilledInvestorWithAccount,
) satisfies Condition;

export const isNotPrefilledTransaction: Condition = not(isPrefilledTransaction);

export const hasAnyOfAccountType = (array: COMP_ARRAY): Condition => {
  return Conditions.array.for(array).rowCount.gt(0, false);
};

const IS_NOT_SELECTED_ACCOUNT: ArrayFilter[] = [
  Filters.array.isDifferent(INVE_FIELDS.INVE43_existingAccountNo.asIndex(), {
    $ref: COMP_FIELDS.COMP1_accountNo,
  }),
];

export const hasZeroBalance: Condition = Conditions.fields.eq(COMP_FIELDS.COMP5_balance, '0');

export const hasSinglePac = Conditions.fields.isEqual(COMP_FIELDS.COMP62_hasMultiplePacs, Booleans.False);
export const hasMultiplePacs = Conditions.fields.isEqual(COMP_FIELDS.COMP62_hasMultiplePacs, Booleans.True);

export const hasPacAmount = Conditions.fields.isFilled(COMP_FIELDS.COMP35_pacAmount);

export const isPacDollarSplit = Conditions.fields.eq(COMP_FIELDS.COMP38_pacSplitOption, AllocationMethod.AMOUNT);
export const isPacPercentageSplit = Conditions.fields.eq(
  COMP_FIELDS.COMP38_pacSplitOption,
  AllocationMethod.PERCENTAGE,
);

export const formatPacAllocation = (amount: string) => buildAmountOrPercentageTemplateValue(amount, isPacDollarSplit);

const isSelectedAccountPlan = (plan: Plans): Condition => Conditions.fields.eq(COMP_FIELDS.COMP2_plan, plan);
const isSelectedAccountContractType = (type: ContractTypes): Condition =>
  Conditions.fields.eq(COMP_FIELDS.COMP4_productType, type);

export const PREFILL_SOURCE_PLAN_RULES = buildSourcePlanRules({
  isPlan: isSelectedAccountPlan,
  isSourceAccountContractType: isSelectedAccountContractType,
  isSourceAccountContractTypeForFHSACondition: isSelectedAccountContractType,
  isContractholder: Conditions.fields.eq(COMP_FIELDS.COMP63_spousalFlag, Contributor.CONTRACTHOLDER),
  isSpousal: Conditions.fields.eq(COMP_FIELDS.COMP63_spousalFlag, Contributor.SPOUSAL),
  isExSpouse: Conditions.fields.eq(COMP_FIELDS.COMP64_purchaserMemberContractholder, ParticipantOrExSpouse.EX_SPOUSE),
  isParticipant: Conditions.fields.eq(
    COMP_FIELDS.COMP64_purchaserMemberContractholder,
    ParticipantOrExSpouse.PARTICIPANT,
  ),
  mode: 'prefill',
});

export const hasAtLeastTwoExistingAccounts: Condition = Conditions.fields.gte(
  INVE_FIELDS.INVE42_existingAccountsCount,
  2,
);

export const hasAtLeastOneExistingContractMatchingAccountProductType: Condition = or(
  ...Object.values(ContractTypes).map(type =>
    and(
      Conditions.fields.eq(COMP_FIELDS.COMP4_productType, type),
      Conditions.query.hasAnyRowInWhere(
        INVE_FIELDS.INVE44_existingAccountProductType.asQuery(),
        [type],
        IS_NOT_SELECTED_ACCOUNT,
      ),
    ),
  ),
);

const HAS_SAME_CONTRACT_TYPE_AS_SELECTED_ACCOUNT: ArrayFilter = Filters.array.isEqual(
  INVE_FIELDS.INVE44_existingAccountProductType.asIndex(),
  {$ref: COMP_FIELDS.COMP4_productType},
);

export const hasAccountMatchingPlanRules: Condition = or(
  ...PREFILL_SOURCE_PLAN_RULES.map(rule =>
    and(
      ...rule.conditions,
      hasAnyExistingAccountAllowedForRule(
        rule,
        rule.plan === Plans.FHSA
          ? IS_NOT_SELECTED_ACCOUNT
          : [...IS_NOT_SELECTED_ACCOUNT, HAS_SAME_CONTRACT_TYPE_AS_SELECTED_ACCOUNT],
      ),
    ),
  ),
);

export const sourceAccountHasSameGuarantee: Condition = Conditions.fields.eq(PRV_COMP_FIELDS.COMP3_guarantee, {
  $ref: 'C13',
});

export const hasIrrevocableBeneficiaryInPrefilledAccount = Conditions.query.hasAnyRowIn(
  COMP_FIELDS.COMP33_beneficiaryRevocableFlag.asQuery(),
  [Booleans.False],
);

const irrevocableBeneficiaryCount = {
  $query: COMP_FIELDS.COMP33_beneficiaryRevocableFlag.asQuery(),
  filters: [Filters.value.isIn([Booleans.False])],
  transform: 'count' as const,
};

export const hasExactlyOneIrrevocableBeneficiaryInPrefilledAccount: Condition = {
  field: irrevocableBeneficiaryCount,
  operator: '=',
  value: 1,
};

export const hasMultipleIrrevocableBeneficiariesInPrefilledAccount: Condition = {
  field: irrevocableBeneficiaryCount,
  operator: '>',
  value: 1,
};

export const firstIrrevocableBeneficiaryInPrefilledAccount = (field: string): GeneratedValue => ({
  $generated: {
    $first: {
      array: COMP_ARRAYS.COMP30_beneficiaries,
      field,
      filters: [
        Filters.array.isEqual(COMP_FIELDS.COMP33_beneficiaryRevocableFlag.asIndex(), {
          $ref: Booleans.False,
        }),
      ],
    },
  },
});
